/**
 * Baseline / meaningful predicates pro object-popup session draft.
 * Parita KMP `WorkflowNoteHelpers` + web excavation gate.
 */
import type { ObjectPopupMaterialDraftRow, WorkflowObjectNoteDraft } from './object-popup-local-draft.types';

/** Má excavation snapshot smysl pro draft persist / commit. */
export function hasMeaningfulExcavationData(ed: Record<string, unknown> | null | undefined): boolean {
  return !!(
    ed &&
    ((Array.isArray(ed['excavationSurfaces']) && (ed['excavationSurfaces'] as unknown[]).length > 0) ||
      (Array.isArray(ed['obnovaSurfaces']) && (ed['obnovaSurfaces'] as unknown[]).length > 0) ||
      ed['excavationLengthM'] != null ||
      ed['excavationMaterial'] ||
      ed['excavationMaterialSubtype'] ||
      ed['removalBeyondScope'] ||
      ed['removalBeyondScopeQuantity'] != null ||
      ed['removalBeyondScopeWidthM'] != null ||
      ed['removalBeyondScopeDepthM'] != null ||
      ed['removalBeyondScopeLengthM'] != null ||
      ed['zakopanyLengthM'] != null ||
      ed['workBeyondScopeZakopanyWidthM'] != null ||
      ed['restorationZakopanyWidthM'] != null ||
      ed['excavationDeepeningCm'] != null ||
      ed['workBeyondScopeZakopany'] ||
      ed['workBeyondScopeZakopanyM2'] != null ||
      ed['restorationZakopany'] ||
      ed['restorationZakopanyM2'] != null ||
      (Array.isArray(ed['zakopanySurfaces']) && (ed['zakopanySurfaces'] as unknown[]).length > 0) ||
      (Array.isArray(ed['specialRemovalRows']) && (ed['specialRemovalRows'] as unknown[]).length > 0))
  );
}

export function serializeWorkflowObjectNotesForBaseline(
  list: readonly WorkflowObjectNoteDraft[] | null | undefined,
): string {
  if (!Array.isArray(list) || list.length === 0) return '[]';
  return JSON.stringify(
    list.map((e) => ({
      id: e.id,
      text: e.text,
      phaseCode: e.phaseCode,
      phaseLabel: e.phaseLabel ?? null,
      createdAt: e.createdAt,
      authorEmail: e.authorEmail,
      status: e.status ?? null,
    })),
  );
}

function parseWorkflowObjectNotesFromBaselineJson(
  baselineJson: string | null | undefined,
): WorkflowObjectNoteDraft[] {
  if (!baselineJson || !baselineJson.trim()) return [];
  try {
    const parsed = JSON.parse(baselineJson) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: WorkflowObjectNoteDraft[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== 'object') continue;
      const o = item as Record<string, unknown>;
      const id = typeof o['id'] === 'string' ? o['id'].trim() : '';
      const text = typeof o['text'] === 'string' ? o['text'].trim() : '';
      if (!id || !text) continue;
      out.push({
        id,
        text: text.slice(0, 500),
        phaseCode: typeof o['phaseCode'] === 'string' ? o['phaseCode'] : '',
        phaseLabel: typeof o['phaseLabel'] === 'string' ? o['phaseLabel'] : null,
        createdAt: typeof o['createdAt'] === 'string' ? o['createdAt'] : '',
        authorEmail: typeof o['authorEmail'] === 'string' ? o['authorEmail'] : '',
        status: typeof o['status'] === 'string' ? o['status'] : null,
      });
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Apply a local-draft notes overlay without dropping server notes from other phases / authors.
 * Parita KMP `mergeWorkflowNotesDraftOverlay`.
 */
export function mergeWorkflowNotesDraftOverlay(
  serverNotes: readonly WorkflowObjectNoteDraft[],
  draftNotes: readonly WorkflowObjectNoteDraft[],
  baselineJson: string | null | undefined,
): WorkflowObjectNoteDraft[] {
  const baselineIds = new Set(parseWorkflowObjectNotesFromBaselineJson(baselineJson).map((n) => n.id));
  const draftById = new Map<string, WorkflowObjectNoteDraft>();
  for (const note of draftNotes) draftById.set(note.id, note);
  const draftIds = new Set(draftById.keys());
  const deletedIds = new Set([...baselineIds].filter((id) => !draftIds.has(id)));
  const out: WorkflowObjectNoteDraft[] = [];
  const seen = new Set<string>();
  for (const server of serverNotes) {
    if (deletedIds.has(server.id)) continue;
    const merged = draftById.get(server.id) ?? server;
    out.push(merged);
    seen.add(merged.id);
  }
  for (const draft of draftNotes) {
    if (seen.has(draft.id) || deletedIds.has(draft.id)) continue;
    out.push(draft);
    seen.add(draft.id);
  }
  return out.sort((a, b) => {
    const ta = String(a.createdAt ?? '');
    const tb = String(b.createdAt ?? '');
    return ta < tb ? -1 : ta > tb ? 1 : a.id.localeCompare(b.id);
  });
}

export function serializeMaterialsForBaseline(
  flattened: { sapCode: string; quantity?: number }[] | null | undefined,
): string {
  if (!Array.isArray(flattened) || flattened.length === 0) return '[]';
  const sorted = [...flattened]
    .map((m) => ({ sapCode: String(m.sapCode ?? ''), quantity: Number(m.quantity ?? 0) }))
    .sort((a, b) => a.sapCode.localeCompare(b.sapCode));
  return JSON.stringify(sorted);
}

/** Flatten draft material rows pro baseline diff — parita KMP `buildMaterialsBaselineJson`. */
export function flattenMaterialDraftRowsForBaseline(
  rows: readonly ObjectPopupMaterialDraftRow[],
): { sapCode: string; quantity: number }[] {
  const acc = new Map<string, number>();
  for (const row of rows) {
    const qty = Number(row.quantity ?? 0);
    if (!Number.isFinite(qty)) continue;
    if (row.kind === 'bundle') {
      const id = (row.bundleId ?? row.sapCode ?? '').trim();
      if (!id) continue;
      acc.set(id, (acc.get(id) ?? 0) + qty);
      continue;
    }
    const sap = (row.sapCode ?? '').trim();
    if (!sap) continue;
    acc.set(sap, (acc.get(sap) ?? 0) + qty);
  }
  return Array.from(acc.entries()).map(([sapCode, quantity]) => ({ sapCode, quantity }));
}

export function buildMaterialsBaselineJsonFromDraftRows(
  rows: readonly ObjectPopupMaterialDraftRow[],
): string {
  return serializeMaterialsForBaseline(flattenMaterialDraftRowsForBaseline(rows));
}
